import React, {useState} from 'react'
import Box from "@mui/material/Box"
import Radio from "@mui/material/Radio"
import Button from "react-bootstrap/Button"
import CircularProgress from "@mui/material/CircularProgress"
import classnames from "classnames"
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import {useMediaQuery} from "react-responsive"

import {BaseModal} from "./BaseModal"
import {mediaQueryForMobile} from "../pages/HomePage/HomePage"
import './TaxRegionModal.scss'

export const TAX_REGION_MODAL_ID = 'TaxRegionModal'

// Which of the two rates MarketScan returns applies to this deal. A region
// carries both, and the same RegionID is valid for either — only the displayed
// number differs.
export const rateForDealType = (region, isPurchase) => (
    isPurchase ? region?.retail_tax_rate : region?.lease_tax_rate
)

/**
 * Shown when a ZIP maps to more than one MarketScan tax region.
 *
 * Every option is keyed and valued by `region_id` and nothing else. Verified
 * live: NY 11101 returns several regions that all share 8.875%, and CO 80126
 * returns two regions both named "HIGHLANDS RANCH, DOUGLAS" at 5% and 4%. Rate,
 * name, city and county are therefore display-only — keying on any of them
 * would collapse distinct choices or select the wrong one.
 *
 * Purely presentational: it reports the choice through onApply/onCancel and
 * owns no lookup or persistence. useTaxRegionFlow drives it.
 */
export const TaxRegionModal = (props) => {
    const {
        zipcode,
        regions = [],
        isPurchase = false,
        onApply,
        onCancel,
        isOpen,
        setIsOpen,
    } = props

    const isTabletOrMobile = useMediaQuery({query: mediaQueryForMobile})
    const [selectedRegionId, setSelectedRegionId] = useState(null)
    const [isApplying, setIsApplying] = useState(false)

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        minWidth: isTabletOrMobile ? '' : '520px',
        width: isTabletOrMobile ? '92%' : '520px',
        transform: 'translate(-50%, -50%)',
        // The themed .TaxRegionModal card paints the surface so it tracks
        // light/dark — keep this wrapper bare, same as LocationModal.
        background: 'transparent',
        zIndex: '1400',
        outline: 'none',
    }

    // Backdrop click and Escape both land here via BaseModal, so they must count
    // as a cancel — otherwise the caller's promise would never settle.
    const dismiss = () => {
        if (isApplying) return
        onCancel && onCancel()
        setIsOpen && setIsOpen(null)
    }

    const apply = () => {
        if (selectedRegionId == null || isApplying) return
        setIsApplying(true)
        onApply && onApply(selectedRegionId)
        setIsOpen && setIsOpen(null)
    }

    return (
        <BaseModal isOpen={isOpen} onClose={dismiss} setIsOpen={dismiss} style={style}>
            <Box className={'TaxRegionModal'} data-testid={'tax-region-modal'}>
                <div className={'TaxRegionModal_header'}>
                    <LocationOnOutlinedIcon/>
                    <div id={'modal-modal-title'}>MULTIPLE TAX RATES FOUND</div>
                </div>

                <div className={'TaxRegionModal_body'}>
                    <p className={'TaxRegionModal_description'} id={'modal-modal-description'}>
                        ZIP {zipcode} has multiple available tax regions.
                    </p>

                    <label className={'TaxRegionModal_label'} id={'tax-region-group-label'}>
                        Select a tax region
                    </label>

                    <div
                        className={'TaxRegionModal_list'}
                        role={'radiogroup'}
                        aria-labelledby={'tax-region-group-label'}
                    >
                        {
                            regions.map((region) => {
                                const isSelected = selectedRegionId === region.region_id
                                const rate = rateForDealType(region, isPurchase)

                                return (
                                    <div
                                        key={region.region_id}
                                        className={classnames('TaxRegionModal_row', {selected: isSelected})}
                                        onClick={() => setSelectedRegionId(region.region_id)}
                                    >
                                        <Radio
                                            checked={isSelected}
                                            value={region.region_id}
                                            onChange={() => setSelectedRegionId(region.region_id)}
                                            inputProps={{'aria-label': `${region.region_name || ''} ${rate ? `${rate}%` : ''}`.trim()}}
                                        />
                                        <span className={'TaxRegionModal_row-name'}>
                                            {region.region_name || [region.city, region.county].filter(Boolean).join(', ')}
                                        </span>
                                        <span className={'TaxRegionModal_row-rate'}>
                                            {rate != null ? `${rate}%` : '—'}
                                        </span>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>

                <div className={'TaxRegionModal_footer'}>
                    <Button className={'TaxRegionModal_cancel'} onClick={dismiss} disabled={isApplying}>
                        Cancel
                    </Button>
                    <Button
                        className={classnames('TaxRegionModal_submit', {
                            disabled: selectedRegionId == null || isApplying,
                        })}
                        onClick={apply}
                        disabled={selectedRegionId == null || isApplying}
                    >
                        {isApplying ? <CircularProgress size={16} color="inherit"/> : 'Apply'}
                    </Button>
                </div>
            </Box>
        </BaseModal>
    )
}
